import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { List, MapPin } from 'lucide-react';
import { getDatasets, deleteDataset } from '../../../api/datasets';
import { PageHeader } from '../../../components/layout/PageHeader';
import { DataTable } from '../../../components/ui/DataTable';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { useAuth } from '../../../hooks/useAuth';
export default function DatasetList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Operators may only import files: structure, display and deletion are admin-only.
    const { isSuperAdmin, isAdminCommune } = useAuth();
    const canManage = isSuperAdmin || isAdminCommune;
    const { data, isLoading } = useQuery({ queryKey: ['datasets'], queryFn: getDatasets });
    const deleteMut = useMutation({
        mutationFn: deleteDataset,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['datasets'] });
            queryClient.invalidateQueries({ queryKey: ['custom-tiles'] });
        },
    });
    const columns = [
        { key: 'name', header: 'Nom', render: (d) => _jsx("span", { className: "font-medium", children: d.name }) },
        {
            key: 'mode',
            header: 'Affichage',
            render: (d) => d.display_mode === 'map'
                ? _jsxs("span", { className: "inline-flex items-center gap-1 text-xs text-gray-600", children: [_jsx(MapPin, { size: 13 }), " Carte"] })
                : _jsxs("span", { className: "inline-flex items-center gap-1 text-xs text-gray-600", children: [_jsx(List, { size: 13 }), " Liste"] }),
        },
        { key: 'fields', header: 'Champs', render: (d) => _jsx("span", { className: "text-sm text-gray-600", children: d.fields.length }) },
        { key: 'items', header: 'Éléments', render: (d) => _jsx("span", { className: "text-sm text-gray-600", children: d.item_count }) },
        {
            key: 'import',
            header: 'Dernier import',
            render: (d) => _jsx("span", { className: "text-xs text-gray-500", children: d.last_import_at ? new Date(d.last_import_at).toLocaleString('fr-FR') : 'Jamais' }),
        },
        { key: 'published', header: 'Dans l\'app', render: (d) => _jsx(StatusBadge, { status: d.published ? 'published' : 'draft' }) },
        ...(!canManage ? [] : [{
                key: 'actions',
                header: '',
                render: (d) => (_jsx("button", { onClick: (e) => {
                        e.stopPropagation();
                        if (confirm(`Supprimer « ${d.name} » et ses ${d.item_count} élément(s) ? Les tuiles qui l'affichent disparaîtront de l'app.`))
                            deleteMut.mutate(d.id);
                    }, className: "text-xs text-red-500 hover:text-red-700", children: "Supprimer" })),
            }]),
    ];
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "Donn\u00E9es personnalis\u00E9es", subtitle: "Vos propres listes et cartes, aliment\u00E9es par un fichier Excel, CSV, JSON ou XML", actions: canManage && (_jsx("button", { onClick: () => navigate('/commune/donnees/new'), className: "px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700", children: "Nouvelles donn\u00E9es" })) }), _jsx(DataTable, { columns: columns, data: data ?? [], isLoading: isLoading, emptyMessage: "Aucune donn\u00E9e pour l'instant. Commencez par d\u00E9finir une structure.", onRowClick: (d) => navigate(`/commune/donnees/${d.id}`) })] }));
}
